// ----------------------------------------------------------------------------
// demuxx — AI Stem Separation in the browser (ONNX Runtime Web + Demucs)
// ----------------------------------------------------------------------------
// Splits an audio file into stems (drums, bass, vocals, other)
// entirely client-side using ONNX Runtime Web (WASM / WebGPU).
//
// Reuses:
//   - renderWaveform from @azopsoft/shared/ui (wavedit waveform engine)
//   - legacy-bucket-integration (bucket + export modal)
//   - audiobuffer-to-wav for WAV export
//
// Uses the htdemucs_embedded.onnx model (timcsy/demucs-web-onnx) with a
// dual-input (raw audio + STFT) inference pipeline ported from
// joverval/sdemux / sevagh/demucs.onnx. Requires the model at
// /models/demuxx/htdemucs.onnx — throws on failure, no silent fallback.
// ----------------------------------------------------------------------------

const { qs, qsa, build, alertmessage } = require('../../libs/qs');
const { renderWaveform } = require('@azopsoft/shared/ui');
const audioBufferToWav = require('audiobuffer-to-wav');
const initLegacyBucket = require('./legacy-bucket-integration.js');

// ---- Demuxx Worker ----
// All ONNX inference + STFT runs off-main-thread to keep the UI responsive.
// The worker source is inlined at build time via __DEMUXX_WORKER_SOURCE__.
let demuxxWorker = null;
function getWorker() {
    if (demuxxWorker) return demuxxWorker;
    const workerSource = typeof __DEMUXX_WORKER_SOURCE__ !== 'undefined'
        ? __DEMUXX_WORKER_SOURCE__ : '';
    if (!workerSource) return null;
    const blob = new Blob([workerSource], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    demuxxWorker = new Worker(url);
    return demuxxWorker;
}
function destroyWorker() {
    if (demuxxWorker) {
        try { demuxxWorker.terminate(); } catch (_) {}
        demuxxWorker = null;
    }
}

// ---- Model configuration ----
// Demucs ONNX model. Override at runtime via ?modelUrl=<url>.
// Defaults to /models/demuxx/htdemucs.onnx. Throws on failure — no fallback.
function resolveModelUrl() {
    const override = new URLSearchParams(location.search).get('modelUrl');
    if (override) return new URL(override, location.origin).href;
    return new URL('/models/demuxx/htdemucs.onnx', location.origin).href;
}

// ---- Theme ----
let currentTheme;
try {
  const storedTheme = localStorage.getItem('currentTheme');
  if (storedTheme) currentTheme = JSON.parse(storedTheme);
} catch (e) {}
if (!currentTheme) currentTheme = window.Theme;
const themeColors = Object.values(currentTheme.data);
window.themeColors = themeColors;
window.currentTheme = currentTheme;
window.CONTROLZ = {};

// ---- AudioContext ----
let audioContext = null;
function getAudioContext() {
    if (!audioContext) {
        audioContext = new AudioContext();
        window.audioContext = audioContext;
        window.audioCtx = audioContext;
    }
    if (audioContext.state === 'suspended') audioContext.resume().catch(() => {});
    return audioContext;
}
document.addEventListener('click', () => getAudioContext(), { once: true, capture: true });

// ---- State ----
const store = {
    audioBuffer: null,    // decoded input AudioBuffer
    fileName: '',
    isProcessing: false,
    cancelRequested: false,
    stems: {},            // { stemName: AudioBuffer }
    stemCards: [],        // rendered card elements
    currentSource: null,  // playing BufferSource
    playingStem: null,
    playStartTime: 0,     // AudioContext.currentTime when playback started
    playOffset: 0,        // offset in seconds into the buffer
    playBuffer: null,     // the AudioBuffer currently playing
    playCanvas: null,     // canvas to draw playhead on
    playWaveImages: null, // cached waveform canvases for redraw during playback
    rafId: null,          // requestAnimationFrame id for playhead
    syncSources: [],      // array of BufferSource for sync playback
    syncRafId: null,      // requestAnimationFrame id for sync playhead
    isSyncPlaying: false,
};

// ---- DOM refs ----
const fileInput = qs('#file-input');
const dropzone = qs('#dropzone');
const inputWaveformContainer = qs('#input-waveform-container');
const inputWaveformCanvas = qs('#input-waveform-canvas');
const inputInfo = qs('#input-info');
const separateBtn = qs('#separate-btn');
const cancelBtn = qs('#cancel-btn');
const allToBucketBtn = qs('#all-to-bucket-btn');
const playSyncBtn = qs('#play-sync-btn');
const progressContainer = qs('#progress-container');
const progressFill = qs('#progress-fill');
const progressText = qs('#progress-text');
const stemsList = qs('#stems-list');

// ============================================================================
// FILE LOADING
// ============================================================================

function setupFileLoading() {
    dropzone.addEventListener('click', () => fileInput.click());

    fileInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) loadAudioFile(file);
        // Allow re-selecting the same file
        fileInput.value = '';
    });

    // Shared drop handler for dropzone + input waveform container
    function handleDrop(e) {
        e.preventDefault();
        e.currentTarget.classList.remove('dragover');

        // Bucket item drop
        const itemId = e.dataTransfer.getData('application/x-bucket-item-id');
        if (itemId) {
            (async () => {
                try {
                    if (!demuxxBucket.bucket) return;
                    const dbItems = await demuxxBucket.dbGetAll(demuxxBucket.bucket.bucketId);
                    const item = dbItems.find((i) => i.id === itemId)
                        || demuxxBucket.bucket.items.find((i) => i.id === itemId);
                    if (item) await loadSampleFromBucketItem(item);
                } catch (err) {
                    console.error('[demuxx] bucket drop error:', err);
                }
            })();
            return;
        }

        // OS file drop
        const file = e.dataTransfer.files[0];
        if (file && (file.type.startsWith('audio/') || file.name.match(/\.(wav|mp3|ogg|flac|aac|m4a)$/i))) {
            loadAudioFile(file);
        }
    }

    function handleDragEnterOver(e) {
        const isBucketItem = e.dataTransfer.types.includes('application/x-bucket-item-id');
        const hasFiles = e.dataTransfer.types.includes('Files');
        if (isBucketItem || hasFiles) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            e.currentTarget.classList.add('dragover');
        }
    }

    function handleDragLeave(e) {
        if (e.target === e.currentTarget) e.currentTarget.classList.remove('dragover');
    }

    // Wire up both dropzone and input waveform container as drop targets
    const dropTargets = [dropzone, inputWaveformContainer];
    dropTargets.forEach((target) => {
        ['dragenter', 'dragover'].forEach((evt) => target.addEventListener(evt, handleDragEnterOver));
        target.addEventListener('dragleave', handleDragLeave);
        target.addEventListener('drop', handleDrop);
    });

    separateBtn.addEventListener('click', () => {
        if (store.audioBuffer && !store.isProcessing) processStems();
    });

    cancelBtn.addEventListener('click', () => {
        if (store.isProcessing) {
            store.cancelRequested = true;
            // Tell the worker to stop, then kill it (inference can't be interrupted mid-chunk)
            const w = getWorker();
            if (w) w.postMessage({ type: 'cancel' });
            destroyWorker();
        } else {
            clearStems();
        }
    });

    allToBucketBtn.addEventListener('click', () => {
        sendAllStemsToBucket();
    });

    playSyncBtn.addEventListener('click', () => {
        if (store.isSyncPlaying) {
            stopSyncPlayback();
        } else {
            startSyncPlayback();
        }
    });

    // Click input waveform to play from clicked position; click again to stop
    inputWaveformCanvas.addEventListener('click', (e) => {
        if (!store.audioBuffer) return;
        const handler = inputWaveformCanvas._clickHandler;
        if (handler) handler(e);
    });

    // Input play button — play from start, or stop if already playing
    const inputPlayBtn = qs('#input-play-btn');
    if (inputPlayBtn) {
        inputPlayBtn.addEventListener('click', () => {
            if (!store.audioBuffer) return;
            if (store.playingStem === '__input__') {
                stopPlayback();
                drawWaveformWithPlayhead(inputWaveformCanvas, store.audioBuffer, 0);
            } else {
                startPlayback('__input__', store.audioBuffer, inputWaveformCanvas, 0, inputPlayBtn);
            }
        });
    }
}

// Set up (or reset) the click handler for the input waveform — called after
// a new audio buffer is loaded.
function setupInputWaveformClick() {
    if (!store.audioBuffer) return;
    inputWaveformCanvas._clickHandler = handleWaveformClick(
        inputWaveformCanvas,
        store.audioBuffer,
        '__input__',
        qs('#input-play-btn'),
    );
}

async function loadAudioFile(file) {
    store.fileName = file.name.replace(/\.[^/.]+$/, '');
    const arrayBuffer = await file.arrayBuffer();
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') await ctx.resume();

    let audioBuffer = null;
    if (file.name.toLowerCase().endsWith('.wav')) {
        audioBuffer = parseWavToAudioBuffer(arrayBuffer, ctx);
    }
    if (!audioBuffer) {
        audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
    }
    await setAudioBuffer(audioBuffer, store.fileName);
}

async function loadSampleFromBucketItem(item) {
    if (!item.wav) { alertmessage('Bucket item has no audio data'); return; }
    if (item.type && item.type !== 'wav' && item.type !== 'audio') {
        alertmessage(`Item type "${item.type}" is not audio`);
        return;
    }
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') await ctx.resume();

    let arrayBuffer;
    if (item.wav instanceof ArrayBuffer) {
        arrayBuffer = item.wav;
    } else if (item.wav instanceof Uint8Array) {
        arrayBuffer = item.wav.buffer.slice(item.wav.byteOffset, item.wav.byteOffset + item.wav.byteLength);
    } else if (item.url) {
        const resp = await fetch(item.url);
        arrayBuffer = await resp.arrayBuffer();
    } else {
        arrayBuffer = item.wav;
    }

    let audioBuffer = parseWavToAudioBuffer(arrayBuffer, ctx);
    if (!audioBuffer) {
        try { audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0)); }
        catch (_) { alertmessage('Failed to decode audio from bucket item'); return; }
    }
    if (!audioBuffer || audioBuffer.length === 0) { alertmessage('Decoded audio is empty'); return; }
    await setAudioBuffer(audioBuffer, (item.name || 'bucket_sample').replace(/\.[^/.]+$/, ''));
}

async function setAudioBuffer(audioBuffer, name) {
    store.audioBuffer = audioBuffer;
    store.fileName = name;
    store.stems = {};
    store.stemCards = [];
    stopPlayback();
    stemsList.innerHTML = '<div class="stems-empty">Click Split to separate stems.</div>';
    allToBucketBtn.disabled = true; playSyncBtn.disabled = true;

    // Show waveform, hide dropzone
    dropzone.style.display = 'none';
    inputWaveformContainer.style.display = 'block';
    inputInfo.style.display = 'flex';
    inputInfo.innerHTML = `
        <span class="info-name">${name}</span>
        <span class="info-meta">${audioBuffer.numberOfChannels}ch</span>
        <span class="info-meta">${audioBuffer.sampleRate} Hz</span>
        <span class="info-meta">${formatDuration(audioBuffer.duration)}</span>
        <span class="info-meta">${formatFileSize(audioBuffer.length * audioBuffer.numberOfChannels * 4)}</span>
    `;
    await renderInputWaveform();
    setupInputWaveformClick();
    separateBtn.disabled = false;
    setStatus('ready', '');
    // Auto-split on load
    processStems();
}

async function renderInputWaveform() {
    if (!store.audioBuffer) return;
    const canvas = inputWaveformCanvas;
    const container = inputWaveformContainer;
    let drawWidth = container.clientWidth;
    let drawHeight = container.clientHeight;
    if (drawWidth < 2 || drawHeight < 2) {
        requestAnimationFrame(() => renderInputWaveform());
        return;
    }
    canvas.width = drawWidth;
    canvas.height = drawHeight;

    const isStereo = store.audioBuffer.numberOfChannels > 1;
    const halfHeight = Math.floor(drawHeight / 2);
    const renderHeight = isStereo ? halfHeight : drawHeight;
    const total = store.audioBuffer.length;

    const [lcanv, rcanv] = await Promise.all([
        renderWaveform(store.audioBuffer, { channel: 0, startSample: 0, endSample: total }, drawWidth, renderHeight, 1),
        isStereo
            ? renderWaveform(store.audioBuffer, { channel: 1, startSample: 0, endSample: total }, drawWidth, renderHeight, 1)
            : Promise.resolve(null),
    ]);

    // Cache for playhead redraw
    canvas._waveImages = { l: lcanv, r: rcanv, w: drawWidth, h: drawHeight, half: halfHeight, stereo: isStereo };
    drawWaveformWithPlayhead(canvas, store.audioBuffer, 0);
}

// ============================================================================
// STEM SEPARATION
// ============================================================================

async function processStems() {
    if (store.isProcessing || !store.audioBuffer) return;
    store.isProcessing = true;
    store.cancelRequested = false;
    separateBtn.disabled = true;
    cancelBtn.hidden = false;
    allToBucketBtn.disabled = true; playSyncBtn.disabled = true;
    progressContainer.style.display = 'flex';
    setProgress(0, 'Initializing…');
    setStatus('busy', 'working');

    // Clear previous stems
    stopPlayback();
    store.stems = {};
    stemsList.innerHTML = '';

    try {
        const stems = await separateStems(store.audioBuffer, 'htdemucs', (p, msg) => {
            setProgress(p, msg);
        });
        store.stems = stems;
        renderStemCards(stems);
        setProgress(100, 'Done');
        setStatus('done', 'complete');
        allToBucketBtn.disabled = false; playSyncBtn.disabled = false;
    } catch (err) {
        if (err.message === 'cancelled') {
            setStatus('idle', 'cancelled');
            setProgress(0, 'Cancelled');
        } else {
            console.error('[demuxx] separation error:', err);
            setStatus('error', 'error');
            alertmessage('Separation failed: ' + (err.message || err));
        }
    } finally {
        store.isProcessing = false;
        separateBtn.disabled = false;
        cancelBtn.hidden = true;
        setTimeout(() => { progressContainer.style.display = 'none'; }, 1500);
    }
}

// ---- Worker-based stem separation ----
// All ONNX inference + STFT runs in a Web Worker. The main thread resamples
// to 44.1kHz (workers have no OfflineAudioContext), then sends transferable
// Float32Array channel data and receives progress + stem results.
async function separateStemsViaWorker(audioBuffer, onProgress) {
    const worker = getWorker();
    if (!worker) throw new Error('Web Worker not available');

    const modelUrl = resolveModelUrl();
    const originalSampleRate = audioBuffer.sampleRate;
    const targetSr = 44100;

    // Resample to 44.1kHz on the main thread (workers lack OfflineAudioContext)
    onProgress(22, 'Preparing audio…');
    let inputBuffer = audioBuffer;
    if (audioBuffer.sampleRate !== targetSr) {
        inputBuffer = await resampleAudioBuffer(audioBuffer, targetSr);
    }

    const channels = inputBuffer.numberOfChannels;

    // Extract channel data as transferable ArrayBuffers
    const channelData = [];
    const transferables = [];
    for (let c = 0; c < channels; c++) {
        const data = new Float32Array(inputBuffer.getChannelData(c));
        channelData.push(data.buffer);
        transferables.push(data.buffer);
    }

    return new Promise((resolve, reject) => {
        let settled = false;
        const handler = (e) => {
            const msg = e.data;
            if (msg.type === 'progress') {
                if (!settled && onProgress) onProgress(msg.percent, msg.message);
            } else if (msg.type === 'done') {
                if (settled) return;
                settled = true;
                worker.removeEventListener('message', handler);
                // Reconstruct AudioBuffers from worker stem data
                const { stems, channels: outCh, length, sampleRate: stemSr } = msg;
                const ctx = getAudioContext();
                const result = {};
                for (const [name, channelsArr] of Object.entries(stems)) {
                    const buf = ctx.createBuffer(outCh, length, stemSr);
                    for (let c = 0; c < outCh; c++) {
                        buf.getChannelData(c).set(channelsArr[c]);
                    }
                    result[name] = buf;
                }
                // Resample stems back to original sample rate if needed
                if (stemSr !== originalSampleRate) {
                    Promise.all(Object.keys(result).map(async (name) => {
                        result[name] = await resampleAudioBuffer(result[name], originalSampleRate);
                    })).then(() => resolve(result));
                } else {
                    resolve(result);
                }
            } else if (msg.type === 'error') {
                if (settled) return;
                settled = true;
                worker.removeEventListener('message', handler);
                reject(new Error(msg.message));
            }
        };
        worker.addEventListener('message', handler);
        worker.postMessage({ type: 'separate', channelData, sampleRate: targetSr, modelUrl, origin: location.origin }, transferables);
    });
}

async function separateStems(audioBuffer, _modelId, onProgress) {
    return await separateStemsViaWorker(audioBuffer, onProgress);
}

// ============================================================================
// RESAMPLING (OfflineAudioContext)
// ============================================================================
async function resampleAudioBuffer(audioBuffer, targetSr) {
    if (audioBuffer.sampleRate === targetSr) return audioBuffer;
    const oac = new OfflineAudioContext(audioBuffer.numberOfChannels, Math.ceil(audioBuffer.duration * targetSr), targetSr);
    const src = oac.createBufferSource();
    src.buffer = audioBuffer;
    src.connect(oac.destination);
    src.start(0);
    return await oac.startRendering();
}

// ============================================================================
// STEM CARDS UI
// ============================================================================

function renderStemCards(stems) {
    stemsList.innerHTML = '';
    store.stemCards = [];
    Object.entries(stems).forEach(([name, buffer]) => {
        const card = createStemCard(name, buffer);
        stemsList.appendChild(card);
        store.stemCards.push(card);
    });
}

function createStemCard(name, audioBuffer) {
    const card = document.createElement('div');
    card.className = 'stem-card';
    card.dataset.stem = name;

    // Header
    const header = document.createElement('div');
    header.className = 'stem-header';

    const title = document.createElement('span');
    title.className = 'stem-title';
    title.draggable = true;
    title.dataset.stem = name;
    title.textContent = name.charAt(0).toUpperCase() + name.slice(1);

    const controls = document.createElement('div');
    controls.className = 'stem-controls';

    const toBucketBtn = document.createElement('button');
    toBucketBtn.className = 'stem-btn to-bucket-btn';
    toBucketBtn.dataset.stem = name;
    toBucketBtn.title = 'Send to bucket';
    toBucketBtn.textContent = '\u2192 bucket';

    const downloadBtn = document.createElement('button');
    downloadBtn.className = 'stem-btn download-btn';
    downloadBtn.dataset.stem = name;
    downloadBtn.title = 'Download WAV';
    downloadBtn.textContent = '\u2193';

    controls.appendChild(toBucketBtn);
    controls.appendChild(downloadBtn);
    header.appendChild(title);
    header.appendChild(controls);

    // Waveform container
    const waveContainer = document.createElement('div');
    waveContainer.className = 'stem-waveform-container';
    waveContainer.draggable = true;
    waveContainer.dataset.stem = name;
    const canvas = document.createElement('canvas');
    waveContainer.appendChild(canvas);

    // Info
    const info = document.createElement('div');
    info.className = 'stem-info';
    const durSpan = document.createElement('span');
    durSpan.className = 'stem-duration';
    durSpan.textContent = formatDuration(audioBuffer.duration);
    const chSpan = document.createElement('span');
    chSpan.textContent = `${audioBuffer.numberOfChannels}ch`;
    const srSpan = document.createElement('span');
    srSpan.textContent = `${audioBuffer.sampleRate} Hz`;
    info.appendChild(durSpan);
    info.appendChild(chSpan);
    info.appendChild(srSpan);

    card.appendChild(header);
    card.appendChild(waveContainer);
    card.appendChild(info);

    // Render waveform after the card is in the DOM
    requestAnimationFrame(() => renderStemWaveform(canvas, audioBuffer));

    // Click waveform to play from clicked position; click again to stop
    canvas.addEventListener('click', handleWaveformClick(canvas, audioBuffer, name, null));

    // To bucket
    toBucketBtn.addEventListener('click', () => {
        sendStemToBucket(name, audioBuffer);
    });

    // Download
    downloadBtn.addEventListener('click', () => {
        downloadStem(name, audioBuffer);
    });

    // Drag stem to bucket — set a custom MIME so the bucket panel can read it.
    const dragSources = [title, waveContainer];
    dragSources.forEach((el) => {
        el.addEventListener('dragstart', (e) => {
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('application/x-demuxx-stem', name);
            e.dataTransfer.setData('text/plain', `${store.fileName}_${name}.wav`);
            card.classList.add('dragging');
        });
        el.addEventListener('dragend', () => {
            card.classList.remove('dragging');
        });
    });

    return card;
}

async function renderStemWaveform(canvas, audioBuffer) {
    const container = canvas.parentElement;
    let drawWidth = container.clientWidth;
    let drawHeight = container.clientHeight;
    if (drawWidth < 2 || drawHeight < 2) {
        requestAnimationFrame(() => renderStemWaveform(canvas, audioBuffer));
        return;
    }
    canvas.width = drawWidth;
    canvas.height = drawHeight;

    const isStereo = audioBuffer.numberOfChannels > 1;
    const halfHeight = Math.floor(drawHeight / 2);
    const renderHeight = isStereo ? halfHeight : drawHeight;
    const total = audioBuffer.length;

    try {
        const [lcanv, rcanv] = await Promise.all([
            renderWaveform(audioBuffer, { channel: 0, startSample: 0, endSample: total }, drawWidth, renderHeight, 1),
            isStereo
                ? renderWaveform(audioBuffer, { channel: 1, startSample: 0, endSample: total }, drawWidth, renderHeight, 1)
                : Promise.resolve(null),
        ]);
        // Cache for playhead redraw
        canvas._waveImages = { l: lcanv, r: rcanv, w: drawWidth, h: drawHeight, half: halfHeight, stereo: isStereo };
        drawWaveformWithPlayhead(canvas, audioBuffer, 0);
    } catch (err) {
    }
}

// ============================================================================
// PLAYBACK — click waveform to play, click again to stop, animated playhead
// ============================================================================

// Draw cached waveform image + playhead line at a given time offset (seconds)
function drawWaveformWithPlayhead(canvas, audioBuffer, currentTime) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const imgs = canvas._waveImages;
    if (!imgs) return;

    // Background
    ctx.fillStyle = window.themeColors ? window.themeColors[0] : '#000';
    ctx.fillRect(0, 0, w, h);
    // Waveform image
    ctx.drawImage(imgs.l, 0, 0);
    if (imgs.stereo && imgs.r) ctx.drawImage(imgs.r, 0, imgs.half);

    // Playhead
    const dur = audioBuffer.duration;
    if (dur > 0 && currentTime >= 0) {
        const x = Math.floor((currentTime / dur) * w);
        // Played-region overlay (subtle tint)
        ctx.fillStyle = window.themeColors
            ? `rgba(${parseInt(window.themeColors[9].slice(1,3),16)},${parseInt(window.themeColors[9].slice(3,5),16)},${parseInt(window.themeColors[9].slice(5,7),16)},0.12)`
            : 'rgba(225,225,225,0.12)';
        ctx.fillRect(0, 0, x, h);
        // Playhead line
        ctx.strokeStyle = window.themeColors ? window.themeColors[14] : '#d2d2d2';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, h);
        ctx.stroke();
    }
}

// Start playing an audio buffer from a given offset (seconds).
// `name` identifies which stem/input is playing (for UI state).
// `canvas` is the waveform canvas to animate the playhead on.
// `button` is the optional play button to toggle state on.
function startPlayback(name, audioBuffer, canvas, offset, button) {
    stopPlayback();
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') ctx.resume();

    const src = ctx.createBufferSource();
    src.buffer = audioBuffer;
    src.connect(ctx.destination);
    src.start(0, offset);

    store.currentSource = src;
    store.playingStem = name;
    store.playBuffer = audioBuffer;
    store.playCanvas = canvas;
    store.playStartTime = ctx.currentTime;
    store.playOffset = offset;

    if (button) {
        button.classList.add('playing');
        button.textContent = '\u25A0';
    }

    src.onended = () => {
        if (store.currentSource === src) {
            // Reset playhead to start before stopping
            if (canvas && audioBuffer) drawWaveformWithPlayhead(canvas, audioBuffer, 0);
            stopPlayback();
        }
    };

    animatePlayhead();
}

function animatePlayhead() {
    if (store.rafId) cancelAnimationFrame(store.rafId);
    function tick() {
        if (!store.currentSource || !store.playBuffer || !store.playCanvas) return;
        const ctx = getAudioContext();
        const elapsed = store.playOffset + (ctx.currentTime - store.playStartTime);
        if (elapsed >= store.playBuffer.duration) {
            // Playback finished — reset playhead to start
            drawWaveformWithPlayhead(store.playCanvas, store.playBuffer, 0);
            stopPlayback();
            return;
        }
        drawWaveformWithPlayhead(store.playCanvas, store.playBuffer, elapsed);
        store.rafId = requestAnimationFrame(tick);
    }
    store.rafId = requestAnimationFrame(tick);
}

function stopPlayback() {
    if (store.rafId) {
        cancelAnimationFrame(store.rafId);
        store.rafId = null;
    }
    if (store.currentSource) {
        try { store.currentSource.stop(); } catch (_) {}
        store.currentSource = null;
    }
    store.playingStem = null;
    store.playBuffer = null;
    store.playCanvas = null;
    store.playStartTime = 0;
    store.playOffset = 0;

    // Reset all play buttons
    qsa('.play-btn').forEach((btn) => {
        btn.classList.remove('playing');
        btn.textContent = '\u25B6';
    });
    // Reset input waveform play button (if any)
    const inputPlayBtn = qs('#input-play-btn');
    if (inputPlayBtn) {
        inputPlayBtn.classList.remove('playing');
        inputPlayBtn.textContent = '\u25B6';
    }

    // Also stop sync playback if running
    if (store.isSyncPlaying) stopSyncPlayback();
}

// ---- Sync playback: play all stems simultaneously ----
function startSyncPlayback() {
    if (store.isSyncPlaying || !store.stems) return;
    stopPlayback(); // stop any solo playback first

    const ctx = getAudioContext();
    if (ctx.state === 'suspended') ctx.resume();

    const entries = Object.entries(store.stems);
    if (entries.length === 0) return;

    store.syncSources = [];
    const startTime = ctx.currentTime + 0.05; // small delay for scheduling

    entries.forEach(([name, buffer]) => {
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(ctx.destination);
        src.start(startTime);
        store.syncSources.push({ name, src, buffer });
    });

    store.isSyncPlaying = true;
    store.playStartTime = startTime;
    store.playOffset = 0;
    playSyncBtn.classList.add('playing');
    playSyncBtn.innerHTML = '\u25A0 sync';

    // Animate playheads on all stem canvases
    animateSyncPlayhead();

    // Stop when the longest stem ends
    const maxDuration = Math.max(...entries.map(([, b]) => b.duration));
    const firstSrc = store.syncSources[0];
    if (firstSrc) {
        firstSrc.src.onended = () => {
            if (store.isSyncPlaying) stopSyncPlayback();
        };
    }
    // Safety timeout
    setTimeout(() => { if (store.isSyncPlaying) stopSyncPlayback(); }, (maxDuration + 1) * 1000);
}

function stopSyncPlayback() {
    if (store.syncRafId) {
        cancelAnimationFrame(store.syncRafId);
        store.syncRafId = null;
    }
    store.syncSources.forEach(({ src, buffer }) => {
        try { src.stop(); } catch (_) {}
    });
    store.syncSources = [];
    store.isSyncPlaying = false;

    // Reset all stem waveform playheads
    store.stemCards.forEach((card) => {
        const canvas = card.querySelector('canvas');
        const name = card.dataset.stem;
        const buffer = store.stems[name];
        if (canvas && buffer) drawWaveformWithPlayhead(canvas, buffer, 0);
    });

    playSyncBtn.classList.remove('playing');
    playSyncBtn.innerHTML = '\u25B6 sync';
}

function animateSyncPlayhead() {
    if (store.syncRafId) cancelAnimationFrame(store.syncRafId);
    function tick() {
        if (!store.isSyncPlaying) return;
        const ctx = getAudioContext();
        const elapsed = ctx.currentTime - store.playStartTime;
        // Update playhead on all stem canvases
        store.stemCards.forEach((card) => {
            const canvas = card.querySelector('canvas');
            const name = card.dataset.stem;
            const buffer = store.stems[name];
            if (canvas && buffer && elapsed <= buffer.duration) {
                drawWaveformWithPlayhead(canvas, buffer, elapsed);
            }
        });
        // Check if all stems finished
        const allDone = store.syncSources.every(({ buffer }) => elapsed >= buffer.duration);
        if (allDone) {
            stopSyncPlayback();
            return;
        }
        store.syncRafId = requestAnimationFrame(tick);
    }
    store.syncRafId = requestAnimationFrame(tick);
}

// Click handler for a waveform canvas — play from clicked position, or stop
// if already playing this buffer.
function handleWaveformClick(canvas, audioBuffer, name, button) {
    return (e) => {
        // If currently playing THIS buffer, stop
        if (store.playingStem === name && store.currentSource) {
            stopPlayback();
            // Redraw waveform without playhead
            drawWaveformWithPlayhead(canvas, audioBuffer, 0);
            return;
        }
        // Compute click position → time offset
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, x / rect.width));
        const offset = ratio * audioBuffer.duration;
        startPlayback(name, audioBuffer, canvas, offset, button);
    };
}

// Legacy toggle for play buttons (plays from start)
function toggleStemPlayback(name, audioBuffer, button) {
    if (store.playingStem === name) {
        stopPlayback();
        const card = button.closest('.stem-card');
        if (card) {
            const canvas = card.querySelector('canvas');
            if (canvas) drawWaveformWithPlayhead(canvas, audioBuffer, 0);
        }
        return;
    }
    const card = button.closest('.stem-card');
    const canvas = card ? card.querySelector('canvas') : null;
    startPlayback(name, audioBuffer, canvas, 0, button);
}

// ============================================================================
// BUCKET INTEGRATION
// ============================================================================

function sendStemToBucket(name, audioBuffer) {
    if (!demuxxBucket.bucket) { alertmessage('Bucket not ready'); return; }
    const wav = audioBufferToWav(audioBuffer);
    demuxxBucket.bucket.add({
        source: 'demuxx',
        name: `${store.fileName || 'demuxx'}_${name}.wav`,
        wavLike: new Uint8Array(wav),
        fileType: 'wav',
    });
}

function sendAllStemsToBucket() {
    if (!demuxxBucket.bucket) { alertmessage('Bucket not ready'); return; }
    Object.entries(store.stems).forEach(([name, buffer]) => sendStemToBucket(name, buffer));
}

function downloadStem(name, audioBuffer) {
    const wav = audioBufferToWav(audioBuffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${store.fileName || 'demuxx'}_${name}.wav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ---- Drag stem onto bucket panel ----
// The legacy-bucket-integration drop handler checks for OS files and known
// custom MIME types. We hook into the bucket panel dragover + drop in the
// capture phase to accept application/x-demuxx-stem.
function setupStemDragToBucket() {
    const bucketPanel = document.querySelector('.xtrc-bucket-panel');
    if (!bucketPanel) return;

    // Allow drop (dragover must be cancelled for drop to fire)
    bucketPanel.addEventListener('dragover', (e) => {
        if (e.dataTransfer.types.includes('application/x-demuxx-stem')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
    }, true);

    // Handle the drop
    bucketPanel.addEventListener('drop', (e) => {
        const stemName = e.dataTransfer.getData('application/x-demuxx-stem');
        if (stemName && store.stems[stemName]) {
            e.preventDefault();
            e.stopPropagation();
            sendStemToBucket(stemName, store.stems[stemName]);
        }
    }, true); // capture phase — run before the integration's own handler
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDuration(duration) {
    const m = Math.floor(duration / 60);
    const s = Math.floor(duration % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function setProgress(percent, msg) {
    progressFill.style.width = `${Math.max(0, Math.min(100, percent))}%`;
    progressText.textContent = msg || '';
}

function setStatus(cls, label) { /* no-op: status element removed */ }

function clearStems() {
    stopPlayback();
    store.stems = {};
    store.stemCards = [];
    store.audioBuffer = null;
    store.fileName = '';
    stemsList.innerHTML = '<div class="stems-empty">Drop an audio file and click Split to separate stems.</div>';
    allToBucketBtn.disabled = true; playSyncBtn.disabled = true;
    separateBtn.disabled = true;
    progressContainer.style.display = 'none';
    setProgress(0, '');
    // Reset input area to page-init state so user can drop a new file
    dropzone.style.display = '';
    inputWaveformContainer.style.display = 'none';
    inputInfo.style.display = 'none';
    inputInfo.innerHTML = '';
    const ctx = inputWaveformCanvas.getContext('2d');
    ctx.clearRect(0, 0, inputWaveformCanvas.width, inputWaveformCanvas.height);
    inputWaveformCanvas._clickHandler = null;
}

// ---- WAV parser (handles 24-bit correctly, like breakchop) ----
function parseWavToAudioBuffer(arrayBuffer, ctx) {
    try {
        const view = new DataView(arrayBuffer);
        const riff = readString(view, 0, 4);
        if (riff !== 'RIFF') return null;
        const wave = readString(view, 8, 4);
        if (wave !== 'WAVE') return null;

        let offset = 12;
        let fmt = null, dataOffset = 0, dataLen = 0;
        while (offset < arrayBuffer.byteLength - 8) {
            const chunkId = readString(view, offset, 4);
            const chunkSize = view.getUint32(offset + 4, true);
            if (chunkId === 'fmt ') {
                fmt = {
                    audioFormat: view.getUint16(offset + 8, true),
                    numChannels: view.getUint16(offset + 10, true),
                    sampleRate: view.getUint32(offset + 12, true),
                    byteRate: view.getUint32(offset + 16, true),
                    blockAlign: view.getUint16(offset + 20, true),
                    bitsPerSample: view.getUint16(offset + 22, true),
                };
            } else if (chunkId === 'data') {
                dataOffset = offset + 8;
                dataLen = chunkSize;
            }
            offset += 8 + chunkSize + (chunkSize & 1);
        }
        if (!fmt || !dataLen) return null;
        if (fmt.audioFormat !== 1 && fmt.audioFormat !== 3) return null; // PCM or float

        const numChannels = fmt.numChannels;
        const bitsPerSample = fmt.bitsPerSample;
        const bytesPerSample = bitsPerSample / 8;
        const numSamples = Math.floor(dataLen / (bytesPerSample * numChannels));
        const audioBuffer = ctx.createBuffer(numChannels, numSamples, fmt.sampleRate);

        for (let ch = 0; ch < numChannels; ch++) {
            const channelData = audioBuffer.getChannelData(ch);
            for (let i = 0; i < numSamples; i++) {
                const sampleOffset = dataOffset + (i * numChannels + ch) * bytesPerSample;
                let sample = 0;
                if (bitsPerSample === 16) {
                    sample = view.getInt16(sampleOffset, true) / 32768;
                } else if (bitsPerSample === 24) {
                    const b0 = view.getUint8(sampleOffset);
                    const b1 = view.getUint8(sampleOffset + 1);
                    const b2 = view.getInt8(sampleOffset + 2);
                    sample = ((b2 << 16) | (b1 << 8) | b0) / 8388608;
                } else if (bitsPerSample === 32) {
                    if (fmt.audioFormat === 3) sample = view.getFloat32(sampleOffset, true);
                    else sample = view.getInt32(sampleOffset, true) / 2147483648;
                } else if (bitsPerSample === 8) {
                    sample = (view.getUint8(sampleOffset) - 128) / 128;
                }
                channelData[i] = sample;
            }
        }
        return audioBuffer;
    } catch (_) {
        return null;
    }
}

function readString(view, offset, len) {
    let s = '';
    for (let i = 0; i < len; i++) s += String.fromCharCode(view.getUint8(offset + i));
    return s;
}

// ============================================================================
// INIT
// ============================================================================

const demuxxBucket = initLegacyBucket({
    appName: 'demuxx',
});

function init() {
    setupFileLoading();
    setupStemDragToBucket();
    // Re-render waveforms on resize
    let resizeTimer = null;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            if (store.audioBuffer) renderInputWaveform();
            store.stemCards.forEach((card) => {
                const name = card.dataset.stem;
                if (store.stems[name]) renderStemWaveform(card.querySelector('canvas'), store.stems[name]);
            });
        }, 200);
    });
}

document.addEventListener('DOMContentLoaded', init);
